"use client";

import { SignInButton, UserButton } from "@clerk/nextjs";
import { api } from "@convex/_generated/api";
import { Authenticated, Unauthenticated, useQuery } from "convex/react";
import { Crosshair, Plus, Search, UserIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  BUILDS_ITEMS,
  CATEGORY_ORDER,
  COMMUNITY_ITEMS,
  DATABASE_ITEMS,
  FILTER_ITEMS,
} from "@/lib/navbar-constants";
import { cn } from "@/lib/utils";
import { AdminNav } from "./admin/admin-nav";
import { MobileMenu } from "./mobile-menu";
import { UserInfo } from "./user-info";

function UserButtonWithProfile() {
  return (
    <>
      <Authenticated>
        <UserButton>
          <UserButton.MenuItems>
            <UserButton.Link
              href="/profile"
              label="Profile"
              labelIcon={<UserIcon />}
            />
          </UserButton.MenuItems>
        </UserButton>
      </Authenticated>
      <Unauthenticated>
        <SignInButton>
          <Button
            className="rounded-[5px] border-[#33383C] font-heading text-[#EAEBEC] text-xs uppercase tracking-wider hover:border-[#F5A23E] hover:bg-transparent"
            size="sm"
            variant="outline"
          >
            Sign In
          </Button>
        </SignInButton>
      </Unauthenticated>
    </>
  );
}

export function Navbar() {
  const weapons = useQuery(api.loadouts.queries.getWeapons);

  // Group weapons by category
  const weaponsByCategory = weapons?.reduce(
    (acc, weapon) => {
      if (!acc[weapon.category]) {
        acc[weapon.category] = [];
      }
      acc[weapon.category].push(weapon);
      return acc;
    },
    {} as Record<string, typeof weapons>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-[#1c1f22] border-b bg-[#0C0D0E]/95 font-heading backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <div className="flex items-center gap-8">
          <Link
            aria-label="BF6 Loadouts home"
            className="group flex items-center gap-2.5"
            data-s-event="navbar-logo-home"
            href="/"
          >
            <Image
              alt="BF6 Loadouts logo"
              className="rounded-md transition-opacity group-hover:opacity-80"
              height={32}
              src="/logo.png"
              width={32}
            />
            <span className="font-bold text-[#EAEBEC] text-lg tracking-wide [font-variation-settings:'wght'_700]">
              BF6<span className="text-[#F5A23E]">{"//"}</span>LOADOUTS
            </span>
          </Link>

          {/* Desktop Navigation */}
          <NavigationMenu className="hidden lg:flex">
            <NavigationMenuList className="gap-6">
              <NavigationMenuItem>
                <NavigationMenuTrigger className="h-auto rounded-none bg-transparent p-0 font-heading text-[#A9AFB3] text-sm uppercase tracking-wide hover:bg-transparent hover:text-[#EAEBEC] focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-[#EAEBEC]">
                  Weapons
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-4 p-6 md:w-[700px] md:grid-cols-3 lg:w-[900px] lg:grid-cols-5">
                    <li className="row-span-2">
                      <NavigationMenuLink asChild>
                        <Link
                          className="group/hero relative flex h-full w-full select-none flex-col justify-end overflow-hidden rounded-[8px] border border-[#262A2D] bg-[#141618] p-6 no-underline outline-none transition-colors hover:border-[#F5A23E]/50"
                          data-s-event="navbar-weapons-all"
                          href="/weapons"
                        >
                          <div className="absolute top-4 left-4 h-4 w-4 border-[#F5A23E] border-t-2 border-l-2" />
                          <Crosshair className="h-6 w-6 text-[#F5A23E]" />
                          <div className="mt-4 mb-2 font-bold font-heading text-[#EAEBEC] text-lg tracking-wide">
                            ALL WEAPONS
                          </div>
                          <p className="text-[#8B9297] text-sm leading-tight">
                            Browse all weapons, stats, and builds
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    {weaponsByCategory &&
                      Object.entries(weaponsByCategory)
                        .sort(
                          ([a], [b]) =>
                            CATEGORY_ORDER.indexOf(a) -
                            CATEGORY_ORDER.indexOf(b)
                        )
                        .map(([category, categoryWeapons]) => (
                          <li
                            className="border-[#262A2D] border-l-2 pl-4"
                            key={category}
                          >
                            <div className="mb-3 font-mono text-[#46C8E0] text-[11px] tracking-[0.15em]">
                              {category.toUpperCase()}
                            </div>
                            <ul className="space-y-1">
                              {categoryWeapons.map((weapon) => (
                                <li key={weapon._id}>
                                  <NavigationMenuLink asChild>
                                    <Link
                                      className="block select-none rounded-[5px] p-2 text-[#A9AFB3] leading-none no-underline outline-none transition-colors hover:bg-[#141618] hover:text-[#F5A23E] focus:bg-[#141618] focus:text-[#F5A23E]"
                                      data-s-event={`navbar-weapon-${weapon.name
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                      href={`/weapons/${weapon.slug}`}
                                    >
                                      <div className="font-medium text-sm leading-none">
                                        {weapon.name}
                                      </div>
                                    </Link>
                                  </NavigationMenuLink>
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="h-auto rounded-none bg-transparent p-0 font-heading text-[#A9AFB3] text-sm uppercase tracking-wide hover:bg-transparent hover:text-[#EAEBEC] focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-[#EAEBEC]">
                  Builds
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                    {BUILDS_ITEMS.map((item) => (
                      <ListItem
                        event={item.event}
                        href={item.url}
                        icon={item.icon}
                        key={item.title}
                        title={item.title}
                      >
                        {item.description}
                      </ListItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="h-auto rounded-none bg-transparent p-0 font-heading text-[#A9AFB3] text-sm uppercase tracking-wide hover:bg-transparent hover:text-[#EAEBEC] focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-[#EAEBEC]">
                  Database
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                    {DATABASE_ITEMS.map((item) => (
                      <ListItem
                        event={item.event}
                        href={item.url}
                        icon={item.icon}
                        key={item.title}
                        title={item.title}
                      >
                        {item.description}
                      </ListItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="h-auto rounded-none bg-transparent p-0 font-heading text-[#A9AFB3] text-sm uppercase tracking-wide hover:bg-transparent hover:text-[#EAEBEC] focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-[#EAEBEC]">
                  Discover
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                    {FILTER_ITEMS.map((item) => (
                      <ListItem
                        event={item.event}
                        href={item.url}
                        icon={item.icon}
                        key={item.title}
                        title={item.title}
                      >
                        {item.description}
                      </ListItem>
                    ))}
                    {COMMUNITY_ITEMS.map((item) => (
                      <ListItem
                        event={item.event}
                        href={item.url}
                        icon={item.icon}
                        key={item.title}
                        title={item.title}
                      >
                        {item.description}
                      </ListItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Right side actions */}
        <div className="hidden items-center gap-3 lg:flex">
          {/* Search affordance — no site-wide search exists yet, kept decorative like the community page's disabled search */}
          <div className="relative hidden xl:block">
            <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 text-[#6a7075]" />
            <Input
              className="h-9 w-[190px] rounded-[5px] border-[#262A2D] bg-[#141618] pl-8 font-mono text-[#6a7075] text-[11px] tracking-wider placeholder:text-[#6a7075]"
              disabled
              placeholder="SEARCH ARSENAL"
              type="search"
            />
          </div>
          <Button
            asChild
            className="rounded-[5px] bg-[#F5A23E] font-heading text-[#0a0b0c] text-xs uppercase tracking-wider hover:bg-[#ffbb63]"
            size="md"
            variant="primary"
          >
            <Link data-s-event="navbar-create-button" href="/create">
              <Plus className="mr-2 h-4 w-4" />
              Create
            </Link>
          </Button>
          <AdminNav />
          <UserInfo />
          <UserButtonWithProfile />
        </div>

        {/* Mobile menu */}
        <MobileMenu userButton={<UserButtonWithProfile />} />
      </div>
    </header>
  );
}

type ListItemProps = React.ComponentProps<typeof Link> & {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  event?: string;
};

function ListItem({
  className,
  title,
  children,
  icon: Icon,
  event,
  ...props
}: ListItemProps) {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          className={cn(
            "group/item block select-none space-y-1 rounded-[6px] border border-transparent p-3 leading-none no-underline outline-none transition-colors hover:border-[#262A2D] hover:bg-[#141618] focus:border-[#262A2D] focus:bg-[#141618]",
            className
          )}
          data-s-event={event}
          {...props}
        >
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-[#46C8E0]" />}
            <div className="font-medium text-[#EAEBEC] text-sm leading-none group-hover/item:text-[#F5A23E]">
              {title}
            </div>
          </div>
          <p className="line-clamp-2 text-[#8B9297] text-sm leading-snug">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  );
}
